import { Chess } from 'chess.js';

export const FILES = 'abcdefgh';
export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const PIECE_VALUES = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 20000 };

export function opposite(color) { return color === 'w' ? 'b' : 'w'; }
export function idxToCoord(index) { return FILES[index % 8] + (8 - Math.floor(index / 8)); }
export function coordToIdx(square) {
  if (!/^[a-h][1-8]$/.test(square)) return -1;
  return (8 - Number(square[1])) * 8 + FILES.indexOf(square[0]);
}
export function pieceColor(piece) { return piece ? (piece === piece.toUpperCase() ? 'w' : 'b') : null; }
export function pieceType(piece) { return piece ? piece.toLowerCase() : null; }

function pieceChar(type, color) {
  return color === 'w' ? type.toUpperCase() : type;
}

function mapMove(move) {
  return {
    from: coordToIdx(move.from),
    to: coordToIdx(move.to),
    piece: pieceChar(move.piece, move.color),
    captured: move.captured ? pieceChar(move.captured, opposite(move.color)) : null,
    promotion: move.promotion || null,
    enPassant: move.isEnPassant(),
    castle: move.isKingsideCastle() ? (move.color === 'w' ? 'K' : 'k') : move.isQueensideCastle() ? (move.color === 'w' ? 'Q' : 'q') : null,
    doublePawn: move.isBigPawn(),
    notation: move.san
  };
}

/**
 * Compatibility adapter around chess.js. The rest of the application keeps its
 * compact 0..63 board API while move generation and end-game rules come from a
 * widely tested chess rules implementation.
 */
export class ChessGame {
  constructor(fen = START_FEN) {
    this.loadFEN(fen);
  }

  loadFEN(fen) {
    this._chess = new Chess(fen);
    this._rootFen = this._chess.fen();
    this.history = [];
    this._syncPublicState();
  }

  _syncPublicState() {
    const fen = this._chess.fen();
    const [, turn, castling = '-', ep = '-', half = '0', full = '1'] = fen.split(/\s+/);
    this.board = this._chess.board().flat().map(piece => piece ? pieceChar(piece.type, piece.color) : null);
    this.turn = turn;
    this.castling = {
      K: castling.includes('K'),
      Q: castling.includes('Q'),
      k: castling.includes('k'),
      q: castling.includes('q')
    };
    this.enPassant = ep === '-' ? -1 : coordToIdx(ep);
    this.halfmove = Number(half) || 0;
    this.fullmove = Number(full) || 1;
  }

  clone() {
    const clone = new ChessGame(this._rootFen);
    for (const entry of this.history) {
      clone.move(entry.move.from, entry.move.to, entry.move.promotion || 'q');
    }
    return clone;
  }

  positionKey() {
    return this._chess.hash();
  }

  toFEN() {
    return this._chess.fen();
  }

  kingIndex(color) {
    return this.board.indexOf(color === 'w' ? 'K' : 'k');
  }

  isSquareAttacked(index, byColor) {
    if (!Number.isInteger(index) || index < 0 || index >= 64) return false;
    return this._chess.isAttacked(idxToCoord(index), byColor);
  }

  inCheck(color = this.turn) {
    if (color === this.turn) return this._chess.isCheck();
    const king = this.kingIndex(color);
    return king >= 0 && this.isSquareAttacked(king, opposite(color));
  }

  pseudoMoves(from) {
    return this.legalMoves(from);
  }

  legalMoves(from = null) {
    if (from !== null && (!Number.isInteger(from) || from < 0 || from >= 64)) return [];
    const options = from === null
      ? { verbose: true }
      : { square: idxToCoord(from), verbose: true };
    return this._chess.moves(options).map(mapMove);
  }

  move(from, to, promotion = 'q') {
    if (typeof from === 'string') from = coordToIdx(from);
    if (typeof to === 'string') to = coordToIdx(to);
    if (![from, to].every(index => Number.isInteger(index) && index >= 0 && index < 64)) return null;

    const before = {
      board: this.board.slice(),
      turn: this.turn,
      castling: { ...this.castling },
      enPassant: this.enPassant,
      halfmove: this.halfmove,
      fullmove: this.fullmove,
      fen: this.toFEN()
    };

    let result;
    try {
      result = this._chess.move({ from: idxToCoord(from), to: idxToCoord(to), promotion });
    } catch {
      return null;
    }

    const mapped = mapMove(result);
    this.history.push({ ...before, move: { ...mapped }, notation: result.san });
    this._syncPublicState();
    return mapped;
  }

  undo() {
    const undone = this._chess.undo();
    if (!undone) return false;
    this.history.pop();
    this._syncPublicState();
    return true;
  }

  isInsufficientMaterial() {
    return this._chess.isInsufficientMaterial();
  }

  status() {
    const check = this._chess.isCheck();
    const checkmate = this._chess.isCheckmate();
    const stalemate = this._chess.isStalemate();
    const fifty = this._chess.isDrawByFiftyMoves();
    const repetition = this._chess.isThreefoldRepetition();
    const insufficient = this._chess.isInsufficientMaterial();
    const draw = this._chess.isDraw();
    return {
      turn: this.turn,
      check,
      checkmate,
      stalemate,
      draw,
      fifty,
      repetition,
      insufficient,
      legalMoves: this.legalMoves().length,
      winner: checkmate ? opposite(this.turn) : null
    };
  }

  evaluate(pov = 'w') {
    const status = this.status();
    if (status.checkmate) return status.winner === pov ? 999999 : -999999;
    if (status.draw) return 0;
    let score = 0;
    for (const piece of this.board) {
      if (!piece) continue;
      const value = PIECE_VALUES[pieceType(piece)];
      score += pieceColor(piece) === pov ? value : -value;
    }
    return score;
  }
}

export function chooseBotMove(game, level = 3) {
  const moves = game.legalMoves();
  if (!moves.length) return null;
  if (level <= 1) return moves[Math.floor(Math.random() * moves.length)];

  const bot = game.turn;
  const ordered = list => list.slice().sort((a, b) =>
    (b.captured ? PIECE_VALUES[pieceType(b.captured)] : 0) -
    (a.captured ? PIECE_VALUES[pieceType(a.captured)] : 0));

  if (level === 2) {
    let best = -Infinity;
    let bestMoves = [];
    for (const move of ordered(moves)) {
      const candidate = game.clone();
      candidate.move(move.from, move.to, move.promotion || 'q');
      const score = candidate.evaluate(bot) + (Math.random() * 120 - 60);
      if (score > best + 1) {
        best = score;
        bestMoves = [move];
      } else if (Math.abs(score - best) <= 1) {
        bestMoves.push(move);
      }
    }
    return bestMoves[Math.floor(Math.random() * bestMoves.length)];
  }

  const depth = level >= 5 ? 3 : 2;
  const noise = level === 3 ? 24 : level === 4 ? 5 : 0;
  const minimax = (position, remaining, alpha, beta, maximizing) => {
    const status = position.status();
    if (remaining === 0 || status.checkmate || status.draw) return position.evaluate(bot);
    const nextMoves = ordered(position.legalMoves());
    if (maximizing) {
      let best = -Infinity;
      for (const move of nextMoves) {
        const child = position.clone();
        child.move(move.from, move.to, move.promotion || 'q');
        best = Math.max(best, minimax(child, remaining - 1, alpha, beta, false));
        alpha = Math.max(alpha, best);
        if (beta <= alpha) break;
      }
      return best;
    }

    let best = Infinity;
    for (const move of nextMoves) {
      const child = position.clone();
      child.move(move.from, move.to, move.promotion || 'q');
      best = Math.min(best, minimax(child, remaining - 1, alpha, beta, true));
      beta = Math.min(beta, best);
      if (beta <= alpha) break;
    }
    return best;
  };

  let best = -Infinity;
  let bestMoves = [];
  for (const move of ordered(moves)) {
    const child = game.clone();
    child.move(move.from, move.to, move.promotion || 'q');
    const score = minimax(child, depth - 1, -Infinity, Infinity, false) + (noise ? Math.random() * noise - noise / 2 : 0);
    if (score > best + 0.01) {
      best = score;
      bestMoves = [move];
    } else if (Math.abs(score - best) <= 0.01) {
      bestMoves.push(move);
    }
  }
  return bestMoves[Math.floor(Math.random() * bestMoves.length)];
}
